import React, { useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import MainHeader, { MENU_REPORT } from "../../components/common/MainHeader";
import { WOOD_IMPORT_GRADE_DISPLAY } from "../../constance/dataTypes";
import ReportDetail from "../../components/reports/ReportDetail";
import { URL_BARCODE } from "../../constance/urls";
import { formatComma } from "../../utils/stringFormat";
import ReportExportButton from "../../components/common/ReportExportButton";
import { Row, Col, DatePicker, Form, Typography } from "antd";
import { DATE_FORMAT, HUMAN_DATE_FORMAT } from "../../constance/strings";
import dayjs from "dayjs";

const GRADE_RENDER_LIST = [
  { key: "ab", text: "ab" },
  { key: "c", text: "c" },
  { key: "p", text: "p" },
  { key: "pp", text: "pp" },
  { key: "other", text: "อื่น ๆ" },
];

export default function ReportWoodImportByVolume() {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const date = Form.useWatch("dateRange", form);
  const [grade, setGrade] = useState("ab");
  const [errors, setErrors] = useState({});
  const [queryParams, setQueryParams] = useState(null);

  const ref = useRef();

  const columns = React.useMemo(() => {
    return [
      {
        title: "วันที่",
        dataIndex: "date",
      },
      ...GRADE_RENDER_LIST.map((grade) => ({
        title: grade.text.toUpperCase(),
        dataIndex: grade.key,
        children: [
          {
            title: "ปริมาณ",
            dataIndex: `${grade.key}_volume`,
            render: (text, record) =>
              formatComma(record[`${grade.key}_volume`], 4),
          },
          {
            title: "จำนวนเงิน",
            dataIndex: `${grade.key}_cost`,
            render: (text, record) => formatComma(record[`${grade.key}_cost`]),
          },
          {
            title: "ราคาเฉลี่ย",
            dataIndex: `${grade.key}_cost_mean`,
            render: (text, record) =>
              formatComma(record[`${grade.key}_cost_mean`]),
          },
        ],
      })),
      {
        title: "รวมทุกเกรด",
        children: [
          {
            title: "ปริมาณ",
            dataIndex: "sum_volume",
            render: (text, record) => formatComma(record.sum_volume, 4),
          },
          {
            title: "จำนวนเงินรวม",
            dataIndex: "sum_cost",
            render: (text, record) => formatComma(record.sum_cost),
          },
        ],
      },
    ];
  }, []);

  const validateForm = () => {
    const values = form.getFieldsValue();
    const newErrors = {};
    if (!values.dateRange || values.dateRange.length < 2)
      newErrors.dateRange = t("error_messages.validation.required");
    if (!grade) newErrors.grade = t("error_messages.validation.required");
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const wood_grade_options = WOOD_IMPORT_GRADE_DISPLAY.map((grade) => {
    return { value: grade, key: grade, text: grade.toLocaleUpperCase() };
  });

  const getQueryParams = () => {
    const values = form.getFieldsValue();
    const startDate = dayjs(values.dateRange[0]).format(DATE_FORMAT);
    const endDate = dayjs(values.dateRange[1]).format(DATE_FORMAT);
    const queryString = `date_after=${startDate}&date_before=${endDate}&grade=${grade}`;
    return queryString;
  };

  React.useEffect(() => {
    if (validateForm()) {
      if (ref.current) {
        const params = getQueryParams();
        ref.current.refetch(params);
        setQueryParams(params);
      }
    }
  }, [date, grade]);

  return (
    <div style={{ margin: "24px", marginTop: "7em" }}>
      <MainHeader activeItem={MENU_REPORT} />
      <Typography.Title level={2}>
        {t("menus.report_menus.wood_import_menus.wood_import_by_volume")}
      </Typography.Title>
      <Form
        layout="horizontal"
        form={form}
        initialValues={{
          dateRange: [],
        }}
      >
        <Row gutter={16}>
          <Col sm={24} md={24} lg={6}>
            <Form.Item
              label={t("filter")}
              name="dateRange"
              rules={[
                { required: true, message: errors.dateRange ? "error" : "" },
              ]}
            >
              <DatePicker.RangePicker
                style={{ width: "100%" }}
                placeholder={[
                  t("searchs.placeholders.startDate"),
                  t("searchs.placeholders.endDate"),
                ]}
                format={HUMAN_DATE_FORMAT}
              />
            </Form.Item>
          </Col>
        </Row>
        <div style={{ textAlign: "right" }}>
          {["pdf", "excel"].map((type) => (
            <ReportExportButton
              key={type}
              url={`${URL_BARCODE.REPORT_WOOD_IMPORT_VOLUME_EXPORT}${type}/?${queryParams}`}
              type={type}
              title={`${t(
                "menus.report_menus.wood_import_menus.wood_import_by_volume"
              )}.....`}
              disabled={!queryParams}
            />
          ))}
        </div>
      </Form>
      <ReportDetail
        ref={ref}
        reportUrl={URL_BARCODE.REPORT_WOOD_IMPORT_VOLUME}
        columns={columns}
      />
    </div>
  );
}
